import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  UploadTask,
} from 'firebase/storage'
import { app } from './firebaseConfig'
// Initialize Firebase Storage
const storage = getStorage(app)
// Upload types
export type UploadProgressCallback = (progress: number) => void
export type UploadErrorCallback = (error: Error) => void
export type UploadCompleteCallback = (downloadUrl: string) => void
/**
 * Uploads a file to Firebase Storage with progress tracking
 * @param file The file to upload
 * @param path The storage path (e.g., 'products/thumbnails/product-123.jpg')
 * @param onProgress Progress callback (0-100)
 * @param onError Error callback
 * @param onComplete Completion callback with download URL
 * @param onValidation Optional callback to validate if upload task was created successfully
 * @returns An object with cancel function to abort upload
 */
export const uploadFile = (
  file: File,
  path: string,
  onProgress?: UploadProgressCallback,
  onError?: UploadErrorCallback,
  onComplete?: UploadCompleteCallback,
  onValidation?: (isValid: boolean) => void,
) => {
  try {
    console.log(`Creating storage reference for path: ${path}`)
    // Create a storage reference
    const storageRef = ref(storage, path)
    // Start upload task
    console.log('Initializing upload task')
    const uploadTask = uploadBytesResumable(storageRef, file)
    // Validate that the upload task was created successfully
    if (!uploadTask) {
      console.error('Upload task creation failed')
      if (onValidation) onValidation(false)
      if (onError) onError(new Error('Failed to create upload task'))
      return { cancel: () => {} }
    }
    // Notify that validation passed
    if (onValidation) onValidation(true)
    // Set up task observers
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        // Track upload progress
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
        )
        console.log(`Upload progress: ${progress}%, State: ${snapshot.state}`)
        if (onProgress) onProgress(progress)
      },
      (error) => {
        // Handle errors
        console.error('Upload error:', error)
        // Log detailed error information
        console.error(`Error code: ${error.code}`)
        console.error(`Error message: ${error.message}`)
        // Check for specific Firebase Storage errors
        if (error.code === 'storage/unauthorized') {
          console.error('Permission denied. Check Firebase Storage rules.')
        } else if (error.code === 'storage/canceled') {
          console.error('Upload was canceled by the user or system')
        } else if (error.code === 'storage/unknown') {
          console.error('Unknown error, possibly network related')
        }
        if (onError) onError(error)
      },
      async () => {
        // Handle successful upload
        try {
          console.log('Upload completed, getting download URL')
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref)
          console.log('Download URL obtained successfully')
          // Do NOT await onComplete — it may be async and handle its own errors.
          // Awaiting it would cause onComplete errors to trigger onError,
          // resulting in both success and error messages showing simultaneously.
          if (onComplete) {
            Promise.resolve(onComplete(downloadUrl)).catch((err) => {
              console.warn(
                'onComplete callback error (handled internally):',
                err,
              )
            })
          }
        } catch (error) {
          // This only fires if getDownloadURL fails, NOT from onComplete errors
          console.error('Error getting download URL:', error)
          if (onError) onError(error as Error)
        }
      },
    )
    // Return cancel function
    return {
      cancel: () => {
        console.log('Cancelling upload')
        uploadTask.cancel()
      },
      uploadTask,
    }
  } catch (error) {
    // Handle any exceptions during setup
    console.error('Exception during upload setup:', error)
    if (onValidation) onValidation(false)
    if (onError) onError(error as Error)
    // Return a dummy cancel function
    return {
      cancel: () => {},
    }
  }
}
/**
 * Generates a unique file path for storage
 * @param userId User ID
 * @param fileType Type of file (thumbnail, product, etc.)
 * @param fileName Original filename
 * @returns A unique storage path
 */
export const generateStoragePath = (
  userId: string,
  fileType: 'thumbnail' | 'product' | 'preview' | 'video' | 'banner',
  fileName: string,
): string => {
  const timestamp = Date.now()
  const fileExtension = fileName.split('.').pop()
  const sanitizedFileName = fileName
    .split('.')[0]
    .replace(/[^a-zA-Z0-9]/g, '-')
    .toLowerCase()
  return `users/${userId}/${fileType}s/${sanitizedFileName}-${timestamp}.${fileExtension}`
}
/**
 * Deletes a file from Firebase Storage
 * @param url The full download URL of the file to delete
 * @returns Promise that resolves when deletion is complete
 */
export const deleteFile = async (url: string): Promise<void> => {
  try {
    // Extract the path from the URL
    const fileRef = ref(storage, url)
    await deleteObject(fileRef)
  } catch (error) {
    console.error('Error deleting file:', error)
    throw error
  }
}
/**
 * Gets a download URL for a file
 * @param path The storage path
 * @returns Promise that resolves with the download URL
 */
export const getFileDownloadURL = async (path: string): Promise<string> => {
  try {
    const fileRef = ref(storage, path)
    return await getDownloadURL(fileRef)
  } catch (error) {
    console.error('Error getting download URL:', error)
    throw error
  }
}
export default storage
